import React, { useState, useEffect } from "react";
import { X, MapPin, ExternalLink, Copy, Check, Camera } from "lucide-react";
import supabase from "../supabaseClient";

const addOneHour = (timeStr) => {
  const [h, m] = timeStr.split(":").map(Number);
  const endH = h + 1;
  if (endH >= 24) return "23:45";
  return `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const addThirtyMinutes = (timeStr) => {
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + 30;
  if (total >= 24 * 60) return "23:45";
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const toMins = (t) => { if (!t) return 0; const [h, m] = t.substring(0, 5).split(":").map(Number); return h * 60 + m; };
const fmtTimeMod = (t) => { if (!t) return ""; const [h, m] = t.substring(0, 5).split(":").map(Number); const hr = h === 0 ? 12 : h > 12 ? h - 12 : h; return `${hr}:${String(m).padStart(2,"0")} ${h < 12 ? "AM" : "PM"}`; };

const EventModal = ({
  onClose,
  onSave,
  onDelete,
  onDuplicate,
  members,
  groups,
  externalContacts,
  seasons,
  venues,
  events = [],
  event,
  initialDate,
  initialTime,
}) => {
  const [title, setTitle] = useState(event?.title || "");
  const [date, setDate] = useState(() => {
    if (event?.date) return event.date;
    if (initialDate) {
      const y = initialDate.getFullYear();
      const m = String(initialDate.getMonth() + 1).padStart(2, "0");
      const d = String(initialDate.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    return "";
  });
  const [endDate, setEndDate] = useState(event?.end_date || "");
  const defaultStart = event?.time
    ? event.time.substring(0, 5)
    : initialTime || "09:00";
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(
    event?.end_time ? event.end_time.substring(0, 5) : addOneHour(defaultStart)
  );
  const [allDay, setAllDay] = useState(event?.all_day || false);
  const [description, setDescription] = useState(event?.description || "");
  const [location, setLocation] = useState(event?.location || "");
  const [url, setUrl] = useState(event?.url || "");
  const [assignmentType, setAssignmentType] = useState(
    event?.group_id
      ? "group"
      : event?.member_ids?.length > 0
      ? "multiple"
      : "member"
  );
  const [selectedMember, setSelectedMember] = useState(event?.member_id || "");
  const [selectedGroup, setSelectedGroup] = useState(event?.group_id || "");
  const [selectedMemberIds, setSelectedMemberIds] = useState(
    event?.member_ids || []
  );
  const [repeat, setRepeat] = useState(event?.repeat || "");
  const [repeatEndDate, setRepeatEndDate] = useState(event?.repeat_end_date || "");
  const [notifyMinutes, setNotifyMinutes] = useState(
    event?.notify_minutes_before ?? ""
  );
  const [timeError, setTimeError] = useState("");
  const [copied, setCopied] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!event?.id) return;
    const load = async () => {
      const { data } = await supabase
        .from("event_photos")
        .select("*")
        .eq("event_id", event.id)
        .order("created_at");
      if (data) {
        const withUrls = await Promise.all(
          data.map(async (p) => {
            const { data: signed } = await supabase.storage
              .from("event-photos")
              .createSignedUrl(p.storage_path, 3600);
            return { ...p, url: signed?.signedUrl || "" };
          })
        );
        setPhotos(withUrls);
      }
    };
    load();
  }, [event?.id]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `${event.id}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage.from("event-photos").upload(path, file);
      if (uploadError) throw uploadError;
      const { data: row, error: dbError } = await supabase
        .from("event_photos")
        .insert({ event_id: event.id, storage_path: path })
        .select()
        .single();
      if (dbError) throw dbError;
      const { data: signed } = await supabase.storage
        .from("event-photos")
        .createSignedUrl(path, 3600);
      setPhotos((prev) => [...prev, { ...row, url: signed?.signedUrl || "" }]);
    } catch (err) {
      alert("Error uploading photo: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await supabase.storage.from("event-photos").remove([photo.storage_path]);
      await supabase.from("event_photos").delete().eq("id", photo.id);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } catch (err) {
      alert("Error deleting photo: " + err.message);
    }
  };
  const [selectedContactIds, setSelectedContactIds] = useState(
    event?.external_contact_ids || []
  );
  const [seasonId, setSeasonId] = useState(event?.season_id || "");
  const [smsReminderHours, setSmsReminderHours] = useState(event?.reminder_hours_before ?? "");

  const toggleContact = (id) => {
    setSelectedContactIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const h = hour.toString().padStart(2, "0");
        const m = min.toString().padStart(2, "0");
        const time24 = `${h}:${m}`;
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? "AM" : "PM";
        times.push({ value: time24, label: `${hour12}:${m} ${ampm}` });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const toggleMemberId = (id) => {
    setSelectedMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const buildEventData = () => ({
    ...(event?.id ? { id: event.id } : {}),
    title,
    date,
    end_date: endDate || null,
    time: allDay ? null : startTime,
    end_time: allDay ? null : endTime,
    all_day: allDay,
    description: description || null,
    location: location || null,
    url: url || null,
    member_id: assignmentType === "member" ? (selectedMember || null) : null,
    group_id: assignmentType === "group" ? (selectedGroup || null) : null,
    member_ids: assignmentType === "multiple" ? selectedMemberIds : null,
    repeat: repeat || null,
    repeat_end_date: repeat && repeatEndDate ? repeatEndDate : null,
    notify_minutes_before: notifyMinutes !== "" ? Number(notifyMinutes) : null,
    external_contact_ids: selectedContactIds.length > 0 ? selectedContactIds : null,
    reminder_hours_before: smsReminderHours !== "" ? Number(smsReminderHours) : null,
    season_id: seasonId ? Number(seasonId) : null,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!allDay && startTime >= endTime) {
      setTimeError("End time must be after start time");
      return;
    }
    setTimeError("");
    onSave(buildEventData());
  };

  const handleDelete = () => {
    if (window.confirm("Delete this event?")) {
      onDelete(event.id);
    }
  };

  const handleDuplicate = () => {
    if (onDuplicate) onDuplicate(buildEventData());
  };

  const getConflicts = () => {
    if (allDay || !date || !startTime || !events.length) return [];
    const myIds = assignmentType === "multiple" ? selectedMemberIds
      : assignmentType === "member" && selectedMember ? [parseInt(selectedMember)]
      : [];
    if (!myIds.length) return [];
    const myStart = toMins(startTime);
    const myEnd = endTime ? toMins(endTime) : myStart + 60;
    return events.filter((e) => {
      if (e.id === event?.id || e._virtual || e.all_day || !e.time || e.date !== date) return false;
      const eMemberIds = e.member_ids?.length ? e.member_ids : e.member_id ? [parseInt(e.member_id)] : [];
      if (!myIds.some((id) => eMemberIds.includes(id))) return false;
      const eStart = toMins(e.time);
      const eEnd = e.end_time ? toMins(e.end_time) : eStart + 60;
      return myStart < eEnd && myEnd > eStart;
    });
  };

  const openInMaps = () => {
    if (location) window.open(`https://maps.google.com/?q=${encodeURIComponent(location)}`, "_blank");
  };

  const openUrl = () => {
    if (url) {
      const full = url.startsWith("http") ? url : `https://${url}`;
      window.open(full, "_blank");
    }
  };

  const copyDescription = async () => {
    if (!description) return;
    try {
      await navigator.clipboard.writeText(description);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // fallback: select text
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6 max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800">
            {event ? "Edit Event" : "New Event"}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2 touch-manipulation">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(""); }}
                className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={endDate}
                min={date}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer p-2 touch-manipulation">
              <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="rounded w-5 h-5" />
              <span className="text-sm font-medium text-gray-700">All Day Event</span>
            </label>
          </div>

          {!allDay && (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <select value={startTime} onChange={(e) => { setStartTime(e.target.value); setEndTime(addThirtyMinutes(e.target.value)); setTimeError(""); }}
                    className="w-full px-2 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base">
                    {timeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <select value={endTime} onChange={(e) => { setEndTime(e.target.value); setTimeError(""); }}
                    className="w-full px-2 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base">
                    {timeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              </div>
              {timeError && <p className="text-red-500 text-sm -mt-1">{timeError}</p>}
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">For</label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {["member", "multiple", "group"].map((type) => (
                <button key={type} type="button" onClick={() => setAssignmentType(type)}
                  className={`py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium touch-manipulation capitalize ${
                    assignmentType === type ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-700"
                  }`}>
                  {type === "member" ? "Individual" : type === "multiple" ? "Multiple" : "Group"}
                </button>
              ))}
            </div>
            {assignmentType === "member" && (
              <select value={selectedMember} onChange={(e) => setSelectedMember(e.target.value)}
                className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base" required>
                <option value="">Select family member</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
            {assignmentType === "multiple" && (
              <div className="border border-gray-300 rounded-lg p-2 space-y-1 max-h-40 overflow-y-auto">
                {members.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded touch-manipulation">
                    <input type="checkbox" checked={selectedMemberIds.includes(m.id)} onChange={() => toggleMemberId(m.id)} className="rounded w-4 h-4" />
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
                    <span className="text-sm">{m.name}</span>
                  </label>
                ))}
              </div>
            )}
            {assignmentType === "group" && (
              <select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base" required>
                <option value="">Select group</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Repeat</label>
            <select value={repeat} onChange={(e) => setRepeat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base">
              <option value="">Does not repeat</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            {repeat && (
              <div className="mt-2">
                <label className="block text-xs text-gray-500 mb-1">End repeat <span className="text-gray-400">(optional)</span></label>
                <input type="date" value={repeatEndDate} min={date} onChange={(e) => setRepeatEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remind me</label>
            <select value={notifyMinutes} onChange={(e) => setNotifyMinutes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base">
              <option value="">No reminder</option>
              <option value="10">10 minutes before</option>
              <option value="15">15 minutes before</option>
              <option value="30">30 minutes before</option>
              <option value="60">1 hour before</option>
              <option value="1440">1 day before</option>
            </select>
          </div>

          {venues?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
              <select
                defaultValue=""
                onChange={(e) => { if (e.target.value) setLocation(e.target.value); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              >
                <option value="">Select a saved venue…</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.address}>{v.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
            <div className="relative">
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Home, Soccer Field"
                className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10 text-base" />
              {location && (
                <button type="button" onClick={openInMaps}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 p-1 touch-manipulation" title="Open in Google Maps">
                  <MapPin size={20} />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL (optional)</label>
            <div className="relative">
              <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10 text-base" />
              {url && (
                <button type="button" onClick={openUrl}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 p-1 touch-manipulation" title="Open URL">
                  <ExternalLink size={20} />
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
              {description && (
                <button type="button" onClick={copyDescription}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 p-1 touch-manipulation">
                  {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                  {copied ? "Copied!" : "Copy"}
                </button>
              )}
            </div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              rows="3" />
          </div>

          {/* Season */}
          {seasons?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Season <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                value={seasonId}
                onChange={(e) => setSeasonId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              >
                <option value="">None</option>
                {seasons.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notify external contacts */}
          {externalContacts?.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notify via SMS
              </label>
              <div className="flex flex-wrap gap-2">
                {externalContacts.map((contact) => {
                  const selected = selectedContactIds.includes(contact.id);
                  return (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => toggleContact(contact.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors touch-manipulation ${
                        selected
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "border-gray-200 text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      📱 {contact.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Tagged contacts will receive an SMS when this event is saved or updated.
              </p>
              {selectedContactIds.length > 0 && !allDay && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">SMS reminder</label>
                  <select
                    value={smsReminderHours}
                    onChange={(e) => setSmsReminderHours(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No reminder</option>
                    <option value="1">1 hour before</option>
                    <option value="2">2 hours before</option>
                    <option value="3">3 hours before</option>
                    <option value="12">12 hours before</option>
                    <option value="24">24 hours before</option>
                    <option value="48">48 hours before</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {(() => {
            const conflicts = getConflicts();
            if (!conflicts.length) return null;
            const myIds = assignmentType === "multiple" ? selectedMemberIds
              : assignmentType === "member" && selectedMember ? [parseInt(selectedMember)] : [];
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-amber-800">⚠️ Scheduling conflict</p>
                {conflicts.map((c) => {
                  const eMemberIds = c.member_ids?.length ? c.member_ids : c.member_id ? [parseInt(c.member_id)] : [];
                  const names = myIds.filter((id) => eMemberIds.includes(id)).map((id) => members.find((m) => m.id === id)?.name).filter(Boolean);
                  return (
                    <p key={c.id} className="text-xs text-amber-700 mt-0.5">
                      {names.join(", ")} already has "{c.title}" at {fmtTimeMod(c.time)}
                    </p>
                  );
                })}
              </div>
            );
          })()}

          {/* Photos — edit mode only */}
          {event?.id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative aspect-square">
                      <img
                        src={photo.url}
                        alt=""
                        className="w-full h-full object-cover rounded-lg cursor-pointer"
                        onClick={() => window.open(photo.url, "_blank")}
                      />
                      <button
                        type="button"
                        onClick={() => deletePhoto(photo)}
                        className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-opacity-80 touch-manipulation"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className={`flex items-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg cursor-pointer text-sm font-medium transition-colors ${
                uploading
                  ? "border-gray-200 text-gray-400 pointer-events-none"
                  : "border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600"
              }`}>
                <Camera size={16} />
                <span>{uploading ? "Uploading…" : photos.length > 0 ? "Add Another Photo" : "Add Photo"}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
              </label>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
            {event && (
              <>
                <button type="button" onClick={handleDelete}
                  className="w-full sm:w-auto px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium touch-manipulation">
                  Delete
                </button>
                {onDuplicate && (
                  <button type="button" onClick={handleDuplicate}
                    className="w-full sm:w-auto px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium touch-manipulation">
                    Duplicate
                  </button>
                )}
              </>
            )}
            <button type="button" onClick={onClose}
              className="w-full sm:flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium touch-manipulation">
              Cancel
            </button>
            <button type="submit"
              className="w-full sm:flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium touch-manipulation">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;
